import React, { useState } from "react";
import { X, Users, Loader2, CheckCircle, Search, AlertTriangle } from "lucide-react";

const AssignEmployeesModal = ({ t, styles, theme, assignHook, adminLevels, onClose, onSave, language }) => {
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSave = async () => {
    setSaving(true);
    await onSave();
    setSaving(false);
  };

  const candidates = assignHook?.candidates || [];
  const selectedUserIds = assignHook?.selectedUserIds || [];
  const targetLevel = assignHook?.selectedLevel;

  const toggleUser = (userId) => {
    assignHook?.toggleUser?.(userId);
  };

  const employeeLabel = (employee) =>
    String(
      language === "ar"
        ? (employee.employee?.arabicName || employee.name || employee.fullName || employee.username || employee.email || employee.id)
        : (employee.employee?.sr || employee.name || employee.fullName || employee.username || employee.email || employee.id)
    ).trim();

  const filteredEmployees = candidates.filter((emp) =>
    emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.id?.toString().includes(searchQuery)
  );

  // ── reassignment warning: selected users who already belong to a DIFFERENT level ──
  const currentLevelByUserId = {};
  (adminLevels || []).forEach((level) => {
    (level?.users || []).forEach((u) => {
      currentLevelByUserId[u.id] = level;
    });
  });
  const conflicts = selectedUserIds
    .map((id) => {
      const employee = candidates.find((c) => c.id === id);
      const currentLevel = currentLevelByUserId[id];
      if (!employee || !currentLevel) return null;
      if (targetLevel?.id && currentLevel.id === targetLevel.id) return null;
      const levelName = String(currentLevel.name || "").startsWith("__user__")
        ? t.customSetup
        : currentLevel.name;
      return { employee, levelName };
    })
    .filter(Boolean);

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      backdropFilter: "blur(4px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      animation: "fadeIn 0.3s ease"
    }} onClick={onClose}>
      <div style={{
        backgroundColor: theme === "dark" ? "#1a1f2e" : "#ffffff",
        borderRadius: "16px",
        boxShadow: theme === "dark" ? "0 20px 60px rgba(0,0,0,0.5)" : "0 20px 60px rgba(0,0,0,0.1)",
        maxWidth: "650px",
        width: "90%",
        maxHeight: "85vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        animation: "slideInUp 0.4s ease"
      }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px",
          borderBottom: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
          backgroundColor: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"
        }}>
          <div>
            <h2 style={{
              fontSize: "20px",
              fontWeight: "700",
              color: theme === "dark" ? "#f1f5f9" : "#1e293b",
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "12px"
            }}>
              <Users size={24} style={{ color: "#2e7cba" }} />
              {t.assignEmployees || "Assign Employees"}
            </h2>
            <p style={{
              fontSize: "13px",
              color: theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
              margin: "6px 0 0 0"
            }}>
              Select employees to assign this admin level to
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
              transition: "all 0.2s ease",
              padding: "8px",
              borderRadius: "6px"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = theme === "dark" ? "#f1f5f9" : "#1e293b";
              e.currentTarget.style.backgroundColor = theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)";
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Search Bar */}
        <div style={{
          padding: "16px 24px",
          borderBottom: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "10px 14px",
            borderRadius: "8px",
            border: `2px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
            backgroundColor: theme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
            transition: "all 0.3s ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#2e7cba";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";
          }}>
            <Search size={18} style={{ color: "#2e7cba" }} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                border: "none",
                background: "none",
                fontSize: "14px",
                color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                fontFamily: "inherit",
                outline: "none",
                padding: "0"
              }}
            />
          </div>
        </div>

        {/* Reassignment warning — informational only, never blocks saving */}
        {conflicts.length > 0 && (
          <div style={{
            margin: "12px 24px 0",
            padding: "12px 14px",
            borderRadius: "10px",
            background: theme === "dark" ? "rgba(217,119,6,0.14)" : "rgba(217,119,6,0.08)",
            border: `1px solid ${theme === "dark" ? "rgba(217,119,6,0.35)" : "rgba(217,119,6,0.25)"}`,
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px",
              fontSize: "12.5px", fontWeight: 700,
              color: theme === "dark" ? "#fbbf24" : "#92400e",
            }}>
              <AlertTriangle size={15} style={{ flexShrink: 0 }} />
              {t.assignMoveWarningTitle}
            </div>
            <div style={{ maxHeight: "96px", overflowY: "auto" }}>
              {conflicts.map(({ employee, levelName }) => (
                <div
                  key={employee.id}
                  style={{
                    fontSize: "12px", lineHeight: 1.7,
                    color: theme === "dark" ? "#fcd34d" : "#92400e",
                  }}
                >
                  {t.assignMoveLine
                    .replace("{name}", employeeLabel(employee))
                    .replace("{level}", levelName)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px 24px",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch"
        }}>
          {assignHook?.loading ? (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "300px",
              color: theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)"
            }}>
              <Loader2 size={32} style={{ animation: "spin 1s linear infinite" }} />
            </div>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {filteredEmployees.length > 0 ? (
                filteredEmployees.map((employee) => {
                  const isAssigned = selectedUserIds.includes(employee.id);
                  return (
                    <button
                      key={employee.id}
                      onClick={() => toggleUser(employee.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "14px 16px",
                        borderRadius: "10px",
                        border: `2px solid ${isAssigned ? "#2e7cba" : theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                        backgroundColor: isAssigned ? "rgba(46, 124, 186, 0.12)" : theme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        background: "none",
                        fontFamily: "inherit",
                        textAlign: "left"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = isAssigned ? "rgba(46, 124, 186, 0.18)" : theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)";
                        e.currentTarget.style.transform = "translateX(4px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isAssigned ? "rgba(46, 124, 186, 0.12)" : theme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)";
                        e.currentTarget.style.transform = "translateX(0)";
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "14px", flex: 1 }}>
                        <input
                          type="checkbox"
                          checked={isAssigned}
                          onChange={() => {}}
                          style={{
                            width: "20px",
                            height: "20px",
                            cursor: "pointer",
                            accentColor: "#2e7cba"
                          }}
                        />
                        {/* <div style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "8px",
                          backgroundColor: "rgba(139, 92, 246, 0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#2e7cba",
                          fontWeight: "700",
                          fontSize: "16px",
                          flexShrink: 0
                        }}>
                          {employee.name?.charAt(0).toUpperCase()}
                        </div> */}
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontWeight: "600",
                            fontSize: "14px",
                            color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                            marginBottom: "4px"
                          }}>
                            {language === "ar"
                              ? (employee.employee?.arabicName || employee.name || employee.fullName || employee.username || employee.email || employee.id)
                              : (employee.employee?.sr || employee.name || employee.fullName || employee.username || employee.email || employee.id)
                            }
                          </div>
                          {employee.email && employee.email !== (language === "ar"
                            ? (employee.employee?.arabicName || employee.name || employee.fullName || employee.username)
                            : (employee.employee?.sr || employee.name || employee.fullName || employee.username)
                          ) && (
                            <div style={{
                              fontSize: "12px",
                              color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)"
                            }}>
                              {employee.email}
                            </div>
                          )}
                        </div>
                      </div>
                      {isAssigned && <CheckCircle size={20} style={{ color: "#2e7cba" }} />}
                    </button>
                  );
                })
              ) : (
                <div style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  color: theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)"
                }}>
                  <Users size={40} style={{ opacity: 0.3, marginBottom: "12px", display: "block", margin: "0 auto" }} />
                  <p style={{ fontSize: "14px", margin: 0 }}>
                    {searchQuery ? "No employees found" : "No employees available"}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          padding: "20px 24px",
          borderTop: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
          backgroundColor: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)"
        }}>
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"}`,
              backgroundColor: theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
              color: theme === "dark" ? "#f1f5f9" : "#1e293b",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.3s ease",
              fontSize: "14px",
              fontFamily: "inherit"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)";
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: "#2e7cba",
              color: "#ffffff",
              fontWeight: "600",
              cursor: saving ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
              opacity: saving ? 0.7 : 1,
              fontSize: "14px",
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.currentTarget.style.backgroundColor = "#1e5a8a";
                e.currentTarget.style.boxShadow = "0 6px 16px rgba(46, 124, 186, 0.3)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }
            }}
            onMouseLeave={(e) => {
              if (!saving) {
                e.currentTarget.style.backgroundColor = "#2e7cba";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
              }
            }}
          >
            {saving && <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />}
            {saving ? "Saving..." : "Assign Employees"}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AssignEmployeesModal;
